import { AnimatedContentLoader, Button, Prompt, SearchInput, TeaserView } from '@components'
import { FeatureWrapper, PermissionBoundary, useBusinessContext } from '@context'
import {
  deleteInvitation,
  deleteUsership,
  fetchInvitations,
  fetchPeople,
  IUsershipPerson
} from '@query'
import { usePermissionGroups } from '@root/query/hooks'
import { createFullName } from '@root/utils'
import { PLUS_TRANSACTION_LIMIT } from '@root/constants'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaTrashAlt } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { breakpointNumberValues } from '@styles'
import { InvitationModal } from './InvitationModal'
import { PeopleTableHeaderRow, PersonCell, StatusCell, ActionsCell } from './PeopleTable.styled'
import { PeopleTableRow } from './PeopleTableRow'
import {
  buildPeopleTableRows,
  filterPeopleTableRows,
  InvitationListItem,
  PeopleTableRow as PeopleTableRowModel
} from './peopleTableUtils'
import { UserEditForm } from './UserEditForm'

export const PeopleSettings: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [editingUsership, setEditingUsership] = useState<IUsershipPerson | null>(null)
  const [removingRow, setRemovingRow] = useState<PeopleTableRowModel | null>(null)
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const ownerEmail = business?.owner_email

  const { data: peopleData, isLoading: isPeopleLoading } = useQuery({
    queryKey: [slug, 'people'],
    queryFn: () => fetchPeople({ slug }, { page_size: 1000 })
  })
  const { data: invitationsData, isLoading: isInvitationsLoading } = useQuery({
    queryKey: [slug, 'invitations'],
    queryFn: () => fetchInvitations({ slug }, { page_size: 9999 })
  })
  const { data: permissionGroups, isLoading: isGroupsLoading } = usePermissionGroups()

  const userships = peopleData?.results || []
  const invitations = invitationsData?.results || []
  const isLoading = isPeopleLoading || isInvitationsLoading || isGroupsLoading

  const rows = useMemo(
    () =>
      buildPeopleTableRows({
        userships,
        invitations: invitations as InvitationListItem[],
        groups: permissionGroups ?? [],
        createFullName
      }),
    [userships, invitations, permissionGroups]
  )

  const filteredRows = useMemo(() => filterPeopleTableRows(rows, search), [rows, search])

  const deleteUsershipMutation = useMutation({
    mutationFn: (usershipId: number) => deleteUsership({ slug, usershipId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug, 'people'] })
      setRemovingRow(null)
      toast.success(t('general.success'))
    },
    onError: (error: unknown) => {
      const { status } = (error as { status?: number }) ?? {}
      if (status === 423) {
        toast.error(t('settings.people.cantDeleteYourself'))
      } else {
        toast.error(t('general.error'))
      }
    }
  })

  const deleteInvitationMutation = useMutation({
    mutationFn: (invitationId: number) => deleteInvitation({ slug, invitationId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug, 'invitations'] })
      setRemovingRow(null)
      toast.success(t('general.success'))
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  const isRemoving = deleteUsershipMutation.isPending || deleteInvitationMutation.isPending

  const confirmRemove = async () => {
    if (!removingRow) return
    if (removingRow.kind === 'member') {
      await deleteUsershipMutation.mutateAsync(removingRow.usership.id)
    } else {
      await deleteInvitationMutation.mutateAsync(removingRow.invitation.id)
    }
  }

  return (
    <SettingsSubPageLayout contentPadding="none">
      <FeatureWrapper
        feature="unlimited_users"
        alternativeContent={
          <TeaserWrapper>
            <TeaserView
              header={t('teaser.people.header')}
              subHeader={t('teaser.people.subHeader')}
              features={[
                t('teaser.feature.1'),
                t('teaser.feature.4', { count: PLUS_TRANSACTION_LIMIT })
              ]}
            />
          </TeaserWrapper>
        }
      >
        <Card>
          <CardHeader>
            <HeaderText>
              <Title>{t('settings.people.peopleTitle')}</Title>
              <Counts>
                {t('settings.people.counts', {
                  members: userships.length,
                  invited: invitations.length
                })}
              </Counts>
            </HeaderText>
            <HeaderActions>
              <SearchWrapper>
                <SearchInput
                  placeholder={t('settings.people.searchPlaceholder')}
                  onSearch={setSearch}
                />
              </SearchWrapper>
              <PermissionBoundary>
                <InviteButton
                  icon={<FaPlus />}
                  onClick={() => setIsModalOpen(true)}
                  title={t('settings.people.sendInvitation')}
                >
                  {t('settings.people.sendInvitation')}
                </InviteButton>
              </PermissionBoundary>
            </HeaderActions>
          </CardHeader>

          <PeopleTableHeaderRow>
            <PersonCell>{t('settings.people.columns.person')}</PersonCell>
            <div className="col-role">{t('settings.people.columns.role')}</div>
            <StatusCell>{t('settings.people.columns.status')}</StatusCell>
            <ActionsCell />
          </PeopleTableHeaderRow>

          <AnimatedContentLoader isLoading={isLoading}>
            {filteredRows.map(row => {
              const isOwner =
                row.kind === 'member' &&
                ownerEmail?.toLowerCase() === row.usership.email?.toLowerCase()

              return (
                <PeopleTableRow
                  key={row.key}
                  row={row}
                  removeDisabled={Boolean(isOwner)}
                  removeDisabledTooltip={
                    isOwner ? t('settings.people.cannotDeleteOwner') : undefined
                  }
                  onEdit={
                    row.kind === 'member' ? () => setEditingUsership(row.usership) : undefined
                  }
                  onRemove={() => setRemovingRow(row)}
                />
              )
            })}
          </AnimatedContentLoader>

          <BottomSpacer />
        </Card>
      </FeatureWrapper>

      <InvitationModal isVisible={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {editingUsership && (
        <UserEditForm
          usership={editingUsership}
          isVisible={Boolean(editingUsership)}
          onClose={() => setEditingUsership(null)}
        />
      )}

      <Prompt
        isVisible={Boolean(removingRow)}
        title={
          removingRow?.kind === 'member'
            ? t('settings.people.deleteUser')
            : t('settings.people.remove')
        }
        description={
          removingRow?.kind === 'member'
            ? t('settings.people.deleteUserConfirm', {
                userEmail: removingRow.usership.email
              })
            : t('settings.people.deleteUserConfirm', {
                userEmail: removingRow?.primary
              })
        }
        onClose={() => setRemovingRow(null)}
        buttons={[
          {
            intent: 'default',
            text: t('general.cancel'),
            action: () => setRemovingRow(null)
          },
          {
            intent: 'danger',
            text: t('general.delete'),
            icon: <FaTrashAlt />,
            action: () => confirmRemove(),
            showSpinner: isRemoving,
            disabled: isRemoving
          }
        ]}
      />
    </SettingsSubPageLayout>
  )
}

const TeaserWrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.md}rem;
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.neutralWhite};
  min-height: 100%;
`

const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.lg}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
`

const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const Title = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const Counts = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  white-space: nowrap;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
  width: 100%;
  container-type: inline-size;
`

/**
 * Hide label (`useScreen().sm` → children null):
 * viewport < md, or tight dual-pane actions row.
 */
const InviteButton = styled(Button)`
  @media (max-width: ${breakpointNumberValues.md - 1}px) {
    > span {
      display: none;
    }
  }

  @container (max-width: 560px) {
    > span {
      display: none;
    }
  }
`

const SearchWrapper = styled.div`
  flex: 1;
  min-width: 0;
`

const BottomSpacer = styled.div`
  height: ${({ theme }) => theme.spacing.sm}rem;
`
